"use client";
import { useEffect, useRef, useState } from "react";

interface IExplainer {
  text: string;
  info?: string;
  description?: string;
  backgroundImage: string;
  handleNext: () => void;
}

export function TryingDurationExplainer1({
  text,
  info,
  description,
  backgroundImage,
  handleNext,
}: IExplainer) {
  const [showInfo, setShowInfo] = useState(false);
  const [visibleWords, setVisibleWords] = useState<number[]>([]);
  const [visibleDescription, setVisibleDescription] = useState<number[]>([]);
  const words = text.split(" ");
  const descriptionWords = description?.split(" ") || [];
  const nextRef = useRef(false);
  const autoNavTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!info) return;
    const infoTimer = setTimeout(() => {
      setShowInfo(true);
    }, 500);

    return () => {
      clearTimeout(infoTimer);
    };
  }, [info]);

  useEffect(() => {
    if (info && !showInfo) return;

    const wordsTimer = setTimeout(() => {
      const animateWords = (
        words: string[],
        setVisible: (index: number) => void
      ) => {
        words.forEach((_, index) => {
          setTimeout(() => {
            setVisible(index);
          }, index * 300);
        });
      };

      animateWords(words, (index: number) =>
        setVisibleWords((prev) => [...prev, index])
      );
    }, 500);

    return () => {
      clearTimeout(wordsTimer);
    };
  }, [words, info, showInfo]);

  useEffect(() => {
    if (
      visibleWords.length === words.length &&
      words.length > 0 &&
      descriptionWords.length > 0
    ) {
      const descriptionTimer = setTimeout(() => {
        const animateDescription = () => {
          descriptionWords.forEach((_, index) => {
            setTimeout(() => {
              setVisibleDescription((prev) => [...prev, index]);
            }, index * 100);
          });
        };
        animateDescription();
      }, 500);

      return () => {
        clearTimeout(descriptionTimer);
      };
    }
  }, [visibleWords.length, words.length, descriptionWords]);

  // Set auto-nav timer when animations complete
  useEffect(() => {
    const totalWords = words.length;
    const totalDescriptionWords = descriptionWords.length;

    if (
      visibleWords.length >= totalWords &&
      visibleDescription.length >= totalDescriptionWords &&
      totalWords > 0 &&
      !nextRef.current
    ) {
      nextRef.current = true;
      autoNavTimerRef.current = setTimeout(() => {
        handleNext();
      }, 3000);
    }
  }, [
    visibleWords,
    visibleDescription,
    words.length,
    descriptionWords.length,
    handleNext,
  ]);

  // Cleanup timer only on unmount
  useEffect(() => {
    return () => {
      if (autoNavTimerRef.current) {
        clearTimeout(autoNavTimerRef.current);
        autoNavTimerRef.current = null;
      }
    };
  }, []);

  return (
    <div
      className="flex-1 mb-4 rounded-[12px] flex flex-col items-center justify-center bg-cover bg-center"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      <div className="p-6 h-full flex flex-col justify-center">
        <div className="flex flex-col gap-2">
          {info && (
            <h3
              className={`text-white captitle-12 transition-all duration-600 ease-in ${
                showInfo ? "opacity-100" : "opacity-0"
              }`}
            >
              {info}
            </h3>
          )}
          <div className="flex flex-col gap-6">
            <p className="text-white title-28">
              {words.map((word, index) => (
                <span
                  key={index}
                  className={`inline-block transition-all duration-800 ease-in ${
                    visibleWords.includes(index) ? "opacity-100" : "opacity-0"
                  }`}
                >
                  {word + "\u00A0"}
                </span>
              ))}
            </p>
            {descriptionWords.length > 0 && (
              <p className="title-16 text-white">
                {descriptionWords.map((word, index) => (
                  <span
                    key={index}
                    className={`inline-block transition-all duration-800 ease-in ${
                      visibleDescription.includes(index)
                        ? "opacity-100"
                        : "opacity-0"
                    }`}
                  >
                    {word + "\u00A0"}
                  </span>
                ))}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
